import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import posthog from 'posthog-js';
import { User, UserRole } from '../types';
import { authService, type RegisterResult } from '../services/authService';
import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errorHandling';
import { clearPendingSchoolCode } from '../lib/pendingSchoolCode';
import { useTenant } from './TenantContext';

/**
 * Set when a session was rejected because the account belongs to a different
 * tenant than the current subdomain — the person who clicked a perfectly valid
 * magic link on the wrong site. slug/name are null when the lookup failed;
 * the UI degrades to a generic "a different school" notice.
 */
export interface CrossTenantAccount {
  email: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tenantMismatch: boolean;
  crossTenantSignout: CrossTenantAccount | null;
  clearCrossTenantSignout: () => void;
  login: (email: string, password: string) => Promise<User>;
  register: (name: string, email: string, role: UserRole, password: string, tenantSlug?: string, schoolCode?: string | null) => Promise<RegisterResult>;
  sendMagicLink: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { tenant, tenantId: currentTenantId } = useTenant();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantMismatch, setTenantMismatch] = useState(false);
  const [crossTenantSignout, setCrossTenantSignout] = useState<CrossTenantAccount | null>(null);
  const tenantIdRef = useRef(currentTenantId);
  tenantIdRef.current = currentTenantId;
  // Id of the profile we've already loaded into `user`. Guards against
  // redundant reloads: on tab refocus supabase-js re-fires SIGNED_IN /
  // TOKEN_REFRESHED for the SAME user, and re-running loadUserProfile would
  // call setUser with a fresh object. That new identity churns CourseContext's
  // refreshData (keyed on `user`), which flips isLoading and unmounts the
  // lesson viewer mid-lesson — resetting the learner's slide position to 0.
  const loadedUserIdRef = useRef<string | null>(null);
  // Id of the profile currently being fetched. `loadedUserIdRef` is only
  // assigned once the users select resolves, so on startup both entry points
  // below (getSession + onAuthStateChange's immediate INITIAL_SESSION) slip
  // past it and fetch concurrently — two profile reads, two setUser identities,
  // and therefore two CourseContext refreshes and two ProtectedRoute effects.
  // This one is set synchronously, before the first await.
  const loadingUserIdRef = useRef<string | null>(null);

  // Detect tenant mismatch: user belongs to a different tenant than the current subdomain
  useEffect(() => {
    if (user && currentTenantId && !user.isSuperAdmin) {
      setTenantMismatch(user.tenantId !== currentTenantId);
    } else {
      setTenantMismatch(false);
    }
  }, [user, currentTenantId]);

  // Tie analytics events to the signed-in user and their tenant. PostHog
  // skips no-ops if the helper module didn't init (no key configured).
  useEffect(() => {
    if (user) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        role: user.role,
        tenant_id: user.tenantId,
        is_super_admin: user.isSuperAdmin,
      });
      if (tenant?.slug) {
        posthog.group('tenant', tenant.slug, {
          name: tenant.name,
          slug: tenant.slug,
        });
      }
    }
  }, [user, tenant?.slug, tenant?.name]);

  // Check for active session and listen for auth changes.
  // Gate on currentTenantId so we never process a session before knowing which tenant this is.
  useEffect(() => {
    if (!currentTenantId) return;

    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session) {
        loadUserProfile(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session) {
        // Same user as already loaded? Skip — re-setting user on every refocus
        // event would unmount the lesson viewer and reset slide progress.
        if (loadedUserIdRef.current === session.user.id) return;
        loadUserProfile(session.user.id);
      } else {
        loadedUserIdRef.current = null;
        setUser(null);
        setIsLoading(false);
      }
    });

    return () => { cancelled = true; subscription.unsubscribe(); };
  }, [currentTenantId]);

  const loadUserProfile = async (userId: string) => {
    // Single-flight: a load for this id is already in progress or already done.
    if (loadingUserIdRef.current === userId || loadedUserIdRef.current === userId) return;
    loadingUserIdRef.current = userId;
    try {
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Detailed error loading profile:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          userId
        });
        throw error;
      }

      if (!profile) {
        console.warn('No profile data returned for user:', userId);
        return;
      }

      // Reject session if user doesn't belong to this tenant — but tell the
      // person WHY. This is the path a cross-tenant magic link lands on: the
      // link verified, the session was real, and silently signing out here
      // reads as "the link did nothing" (auth logs show verify → login →
      // logout two seconds apart). Resolve the account's home tenant BEFORE
      // signOut (the read runs while still authenticated; best-effort).
      if (tenantIdRef.current && !profile.is_super_admin && profile.tenant_id !== tenantIdRef.current) {
        let homeSlug: string | null = null;
        let homeName: string | null = null;
        try {
          // supabase-js returns errors rather than throwing, so re-throw for
          // the catch — otherwise a failed lookup is invisible everywhere.
          const { data: home, error: homeError } = await supabase
            .from('tenants')
            .select('slug, name')
            .eq('id', profile.tenant_id)
            .maybeSingle();
          if (homeError) throw new Error(homeError.message);
          homeSlug = home?.slug ?? null;
          homeName = home?.name ?? null;
        } catch (err) {
          // Non-fatal: the banner degrades to "a different school" with no
          // slug/name, so the person is still told what happened.
          reportError('AuthContext.resolveHomeTenant', err);
        }
        await supabase.auth.signOut();
        loadedUserIdRef.current = null;
        setUser(null);
        setCrossTenantSignout({ email: profile.email ?? null, tenantSlug: homeSlug, tenantName: homeName });
        return;
      }

      loadedUserIdRef.current = profile.id;
      setUser({
        id: profile.id,
        name: profile.name,
        email: profile.email,
        role: profile.role as UserRole,
        avatarUrl: profile.avatar_url || undefined,
        tenantId: profile.tenant_id,
        isSuperAdmin: profile.is_super_admin || false,
      });
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Don't block the app if profile fetch fails
      loadedUserIdRef.current = null;
      setUser(null);
    } finally {
      loadingUserIdRef.current = null;
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<User> => {
    setIsLoading(true);
    try {
      const loggedInUser = await authService.login(email, password, currentTenantId || undefined);
      loadedUserIdRef.current = loggedInUser.id;
      setUser(loggedInUser);
      return loggedInUser;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, role: UserRole, password: string, tenantSlug?: string, schoolCode?: string | null): Promise<RegisterResult> => {
    setIsLoading(true);
    try {
      const result = await authService.register(name, email, role, password, tenantSlug, schoolCode);
      if (result.user) {
        loadedUserIdRef.current = result.user.id;
        setUser(result.user);
      }
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const sendMagicLink = async (email: string) => {
    // The slug is what lets handle_new_user attribute the row if this path ever
    // creates a user; without it the account lands in the fallback tenant and
    // the tenant guard locks the person out of the site they signed up on.
    await authService.sendMagicLink(email, tenant?.slug);
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      loadedUserIdRef.current = null;
      setUser(null);
      // Stop attaching events to the previous user.
      posthog.reset();
      // A school code waiting on this browser belongs to the person leaving.
      clearPendingSchoolCode();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      tenantMismatch,
      crossTenantSignout,
      clearCrossTenantSignout: () => setCrossTenantSignout(null),
      login,
      register,
      sendMagicLink,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Safe variant for public-facing components where auth is optional. */
export const useOptionalAuth = (): AuthContextType | null => {
  return useContext(AuthContext) ?? null;
};